<script setup lang="ts">
import GameArea from '@/components/GameArea.vue';
import MidiDeviceSelector from '@/components/MidiDeviceSelector.vue';
import MusicXmlUploader from '@/components/MusicXmlUploader.vue';
import IconButton from '@/components/IconButton.vue';
import { ChevronLeftIcon } from '@heroicons/vue/24/outline';
import { useDeviceDetection } from '@/composables/useDeviceDetection';
import { provideSheetMusic } from '@/composables/useSheetMusic';
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { provideGameEngine } from '@/composables/useGameEngine';

const sheetMusic = provideSheetMusic();
const gameEngine = provideGameEngine(sheetMusic);
const router = useRouter();
const { isMobileDevice } = useDeviceDetection();
// Start with MIDI tab as that should be done first
const activeTab = ref('midi');

onMounted(()=>{
  sheetMusic.isLoaded.value = false;
  sheetMusic.resetXmlContent();
})

const musicXmlFile = ref<File>();

const handleFileLoaded = (file: File) => {
  musicXmlFile.value = file;
}

const handleBackToSongs = () => {
  musicXmlFile.value = undefined;
}
</script>

<template>
  <div class="flex flex-col">
    <!-- Back button (mobile only) -->
    <IconButton
      v-if="isMobileDevice"
      class="absolute top-4 right-4 z-30"
      variant="text"
      size="sm"
      aria-label="Go back"
      @click="router.back()"
    >
      <ChevronLeftIcon class="w-5 h-5 text-text-tertiary" />
    </IconButton>
    <!-- Back button (desktop) -->
    <div
      v-if="!isMobileDevice"
      class="absolute flex items-center gap-2 px-4 pt-4"
    >
      <IconButton
        variant="text"
        size="sm"
        aria-label="Go back"
        @click="router.back()"
      >
        <ChevronLeftIcon class="w-5 h-5 text-text-tertiary" />
      </IconButton>
    </div>
    
    <!-- Welcome screen with file uploader when no sheet music is loaded -->
    <div 
      v-if="!musicXmlFile" 
      class="flex-1 flex flex-col justify-center items-center p-4 space-y-6"
    >
      <div class="text-center mb-4">
        <p class="text-text-secondary">
          Upload your MusicXML file and play along with your MIDI drum kit
        </p>
      </div>
      
      <!-- Mobile-specific UI with tabs -->
      <div
        v-if="isMobileDevice"
        class="w-full max-w-md overflow-hidden grow flex flex-col max-h-100"
      >
        <!-- Tab Navigation -->
        <div class="flex bg-surface rounded-t-xl">
          <button 
            class="flex-1 py-3 text-center font-medium rounded-tl-xl transition-colors" 
            :class="activeTab === 'midi' ? 'bg-primary text-text-primary' : 'text-text-secondary hover:bg-surface-hover'"
            @click="activeTab = 'midi'"
          >
            <span class="flex items-center justify-center">
              <span class="text-sm mr-2">1</span> Connect MIDI
            </span>
          </button>
          <button 
            class="flex-1 py-3 text-center font-medium rounded-tr-xl transition-colors" 
            :class="activeTab === 'uploader' ? 'bg-primary text-text-primary' : 'text-text-secondary hover:bg-surface-hover'"
            @click="activeTab = 'uploader'"
          >
            <span class="flex items-center justify-center">
              <span class="text-sm mr-2">2</span> Upload Music
            </span>
          </button>
        </div>
        
        <!-- Tab Content Container with fixed height -->
        <div class="relative bg-surface/30 rounded-b-xl flex flex-col grow">
          <transition
            name="tab-fade"
            mode="out-in"
          >
            <!-- MIDI Tab -->
            <div
              v-if="activeTab === 'midi'"
              class="grow flex flex-col p-4 inset-0"
            >
              <MidiDeviceSelector :class="{'py-2': isMobileDevice}" />
              <div class="mt-4 flex justify-end">
                <button 
                  class="px-4 py-2 bg-primary text-text-primary rounded-lg hover:bg-opacity-90 transition-colors" 
                  @click="activeTab = 'uploader'"
                >
                  Continue to Upload →
                </button>
              </div>
            </div>
            <!-- Upload Tab -->
            <div
              v-else-if="activeTab === 'uploader'"
              class="grow flex flex-col p-4 inset-0"
            >
              <MusicXmlUploader
                dense
                @file-loaded="handleFileLoaded"
              />
              <div class="mt-4 flex justify-start">
                <button 
                  class="px-4 py-2 bg-surface text-text-primary rounded-lg hover:bg-surface-hover transition-colors" 
                  @click="activeTab = 'midi'"
                >
                  ← Back to MIDI Setup
                </button>
              </div>
            </div>
          </transition>
        </div>
      </div>
      
      <!-- Desktop UI with guided steps -->
      <div
        v-else
        class="w-full max-w-2xl"
      >
        <div class="mb-6">
          <div class="flex items-center mb-4">
            <div class="bg-primary text-text-primary rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">
              1
            </div>
            <h2 class="text-lg font-medium text-text-primary">
              Connect your MIDI device
            </h2>
          </div>
          <MidiDeviceSelector />
        </div>
        
        <div>
          <div class="flex items-center mb-4">
            <div class="bg-primary text-text-primary rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">
              2
            </div>
            <h2 class="text-lg font-medium text-text-primary">
              Upload your music
            </h2>
          </div>
          <MusicXmlUploader @file-loaded="handleFileLoaded" />
        </div>
      </div>
    </div>
    
    <Transition name="fade">
      <!-- Game area when sheet music is loaded -->
      <!-- using v-show here for init osmd to work -->
      <GameArea
        v-if="musicXmlFile"
        :music-xml-file="musicXmlFile"
        @exit="handleBackToSongs"
        @pause="gameEngine.stopPlayback()"
      >
        <template #header-left>
          <IconButton
            v-if="!isMobileDevice"
            variant="text"
            size="sm"
            aria-label="Go back"
            @click="router.back()"
          >
            <ChevronLeftIcon class="w-5 h-5 text-text-tertiary" />
          </IconButton>
        </template>
      </GameArea>
    </Transition>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.tab-fade-enter-active,
.tab-fade-leave-active {
  position: absolute;
  left: 0;
  right: 0;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.tab-fade-enter-from {
  opacity: 0;
}

.tab-fade-leave-to {
  opacity: 0;
}
</style>
